"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuditResource = exports.TablesResource = exports.TelegramResource = exports.MarketingResource = exports.CustomersResource = void 0;
/** CRM (TZ §31, §32). */
class CustomersResource {
    http;
    constructor(http) {
        this.http = http;
    }
    list(query) {
        return this.http.getPaginated('customers', { query });
    }
    /** Base size, returning share, lifetime value, points owed, segment sizes. */
    summary() {
        return this.http.get('customers/summary');
    }
    /** Includes addresses and loyalty standing; the list does not. */
    get(id) {
        return this.http.get(`customers/${id}`);
    }
    update(id, payload) {
        return this.http.patch(`customers/${id}`, payload);
    }
    /** What a manager checks before a goodwill call. */
    orders(id, query) {
        return this.http.getPaginated(`customers/${id}/orders`, { query });
    }
    /** Blocking keeps the history and the points — it stops them ordering. */
    block(id, reason) {
        return this.http.post(`customers/${id}/block`, { reason });
    }
    unblock(id) {
        return this.http.post(`customers/${id}/unblock`);
    }
    /* ------------------------------ Loyalty --------------------------- */
    loyaltyProgram() {
        return this.http.get('loyalty/program');
    }
    updateLoyaltyProgram(payload) {
        return this.http.patch('loyalty/program', payload);
    }
    loyaltyAccount(customerId) {
        return this.http.get(`customers/${customerId}/loyalty`);
    }
    loyaltyTransactions(customerId, query) {
        return this.http.getPaginated(`customers/${customerId}/loyalty/transactions`, { query });
    }
    /** The most this customer could pay with points on a bill of `total`. */
    loyaltyQuote(customerId, total) {
        return this.http.get(`customers/${customerId}/loyalty/quote`, { query: { total } });
    }
}
exports.CustomersResource = CustomersResource;
/** Promotions and promo codes (TZ §10, §34). */
class MarketingResource {
    http;
    constructor(http) {
        this.http = http;
    }
    listPromotions(query) {
        return this.http.getPaginated('promotions', { query });
    }
    getPromotion(id) {
        return this.http.get(`promotions/${id}`);
    }
    createPromotion(payload) {
        return this.http.post('promotions', payload);
    }
    updatePromotion(id, payload) {
        return this.http.patch(`promotions/${id}`, payload);
    }
    /** Soft delete: orders already priced with it still name it. */
    deletePromotion(id) {
        return this.http.delete(`promotions/${id}`);
    }
    listPromoCodes(query) {
        return this.http.getPaginated('promo-codes', { query });
    }
    getPromoCode(id) {
        return this.http.get(`promo-codes/${id}`);
    }
    createPromoCode(payload) {
        return this.http.post('promo-codes', payload);
    }
    /** The code itself is not patchable — customers already have it. */
    updatePromoCode(id, payload) {
        return this.http.patch(`promo-codes/${id}`, payload);
    }
    deletePromoCode(id) {
        return this.http.delete(`promo-codes/${id}`);
    }
    /** Who redeemed a code and for how much — the campaign's actual result. */
    listPromoCodeUsages(id, query) {
        return this.http.getPaginated(`promo-codes/${id}/usages`, { query });
    }
    /** Checks a code against a cart before the order is placed. */
    validatePromoCode(payload) {
        return this.http.post('promo-codes/validate', payload);
    }
}
exports.MarketingResource = MarketingResource;
/** Telegram bot, group and topic routing (TZ §14, §15). */
class TelegramResource {
    http;
    constructor(http) {
        this.http = http;
    }
    getConfig(branchId) {
        return this.http.get('telegram/config', { query: { branchId } });
    }
    /** Whether Telegram is actually delivering to this deployment. */
    getBotStatus(branchId) {
        return this.http.get('telegram/bot/status', { query: { branchId } });
    }
    /** Points the bot's webhook back at this deployment. */
    connectBot(branchId) {
        return this.http.post('telegram/bot/connect', undefined, {
            query: { branchId },
        });
    }
    saveConfig(payload) {
        return this.http.put('telegram/config', payload);
    }
    listRoutes(branchId) {
        return this.http.get('telegram/routes', { query: { branchId } });
    }
    upsertRoute(payload) {
        return this.http.put('telegram/routes', payload);
    }
    deleteRoute(id) {
        return this.http.delete(`telegram/routes/${id}`);
    }
    /** Sends a probe message so the admin can confirm the chat/topic is right. */
    testRoute(payload) {
        return this.http.post('telegram/routes/test', payload);
    }
}
exports.TelegramResource = TelegramResource;
class TablesResource {
    http;
    constructor(http) {
        this.http = http;
    }
    list(branchId) {
        return this.http.get('tables', { query: { branchId } });
    }
    create(payload) {
        return this.http.post('tables', payload);
    }
    update(id, payload) {
        return this.http.patch(`tables/${id}`, payload);
    }
    /** The live floor of one branch: tables with open orders, calls and bookings. */
    floor(branchId) {
        return this.http.get('tables/floor', { query: { branchId } });
    }
    /** Saves floor-plan positions in one go. */
    saveLayout(items) {
        return this.http.put('tables/layout', { items });
    }
    /** Bookings starting inside `[from, to)`; at most 31 days. */
    listReservations(query) {
        return this.http.get('reservations', { query });
    }
    createReservation(payload) {
        return this.http.post('reservations', payload);
    }
    /** Edits a booking, or moves it on (seated, completed, cancelled, no-show). */
    updateReservation(id, payload) {
        return this.http.patch(`reservations/${id}`, payload);
    }
    remove(id) {
        return this.http.delete(`tables/${id}`);
    }
    /** Issues a new QR token, invalidating printed codes for that table. */
    rotateQr(id) {
        return this.http.post(`tables/${id}/rotate-qr`);
    }
    /** Open calls, oldest first — the queue a waiter works. */
    listCalls(query) {
        return this.http.get('waiter-calls', { query });
    }
    acknowledgeCall(id) {
        return this.http.post(`waiter-calls/${id}/acknowledge`);
    }
    resolveCall(id) {
        return this.http.post(`waiter-calls/${id}/resolve`);
    }
    /**
     * Public: resolves a scanned QR token.
     *
     * `skipAuth` because a guest has no account — the token in the path is the
     * whole credential, and it reaches one table at one branch.
     */
    resolveQr(token) {
        return this.http.get(`t/${token}`, { skipAuth: true });
    }
    /** Public: guest pressing "Call waiter" / "Request bill". */
    call(token, type) {
        return this.http.post(`t/${token}/call`, { type }, { skipAuth: true });
    }
    /** Just the ones nobody has picked up yet. */
    pendingCalls(branchId) {
        return this.http.get('waiter-calls', { query: { branchId, status: 'PENDING' } });
    }
}
exports.TablesResource = TablesResource;
/** Audit log (TZ §37). */
class AuditResource {
    http;
    constructor(http) {
        this.http = http;
    }
    list(query) {
        return this.http.getPaginated('audit', { query });
    }
}
exports.AuditResource = AuditResource;
//# sourceMappingURL=misc.js.map